import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { drugRecallService } from '../services/drugRecallService'
import { DrugRecall } from '../types/drugRecall'
import { format } from 'date-fns'
import './SettingsDrugRecalls.css'

export function SettingsDrugRecalls() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const { data: recalls, isLoading, error } = useQuery<DrugRecall[]>({
    queryKey: ['drug-recalls', filterStatus],
    queryFn: () => drugRecallService.getAll(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => drugRecallService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drug-recalls'] })
    },
  })

  const filteredRecalls = recalls?.filter((r) => filterStatus === 'all' || r.status === filterStatus) || []

  if (isLoading) return <div className="drug-recalls-loading">Loading drug recalls...</div>
  if (error) return <div className="drug-recalls-error">Error loading drug recalls</div>

  return (
    <div className="drug-recalls-page">
      <div className="page-header">
        <h1>Drug Recalls</h1>
        <button className="btn-primary" onClick={() => navigate('/settings/drug-recalls/new')}>
          New Recall
        </button>
      </div>

      <div className="recalls-filters">
        <button
          className={filterStatus === 'all' ? 'active' : ''}
          onClick={() => setFilterStatus('all')}
        >
          All
        </button>
        <button
          className={filterStatus === 'active' ? 'active' : ''}
          onClick={() => setFilterStatus('active')}
        >
          Active
        </button>
        <button
          className={filterStatus === 'resolved' ? 'active' : ''}
          onClick={() => setFilterStatus('resolved')}
        >
          Resolved
        </button>
      </div>

      <div className="recalls-list">
        {filteredRecalls.length === 0 ? (
          <div className="empty-state">
            <p>No drug recalls found</p>
            <button className="btn-primary" onClick={() => navigate('/settings/drug-recalls/new')}>
              Create First Recall
            </button>
          </div>
        ) : (
          filteredRecalls.map((recall) => (
            <div key={recall.id} className="recall-card">
              <div className="recall-header">
                <div>
                  <h3>{recall.title}</h3>
                  <div className="recall-drug">{recall.drugName}</div>
                </div>
                <div className="recall-badges">
                  <span className={`severity-badge severity-${recall.severity}`}>
                    {recall.severity}
                  </span>
                  <span className={`status-badge status-${recall.status}`}>{recall.status}</span>
                </div>
              </div>
              <div className="recall-date">
                Recall Date: {format(new Date(recall.recallDate), 'MMM dd, yyyy')}
              </div>
              {recall.description && <p className="recall-description">{recall.description}</p>}
              <div className="recall-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/settings/drug-recalls/${recall.id}`)}
                >
                  View Details
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/settings/drug-recalls/${recall.id}/affected-patients`)}
                >
                  Affected Patients
                </button>
                <button
                  className="btn-danger"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this recall?')) {
                      deleteMutation.mutate(recall.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
